import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TargetProbeLegRecord } from '@nadoutong/contracts';
import { Ban, Play, Plus, RotateCcw } from 'lucide-react';
import { TARGET_PROBES_KEY, useProbeTargetNow, useResetTargetProbe } from '../../api/useTargetProbes.ts';
import {
  useSetRoutingConfigSkuTarget,
  useSetRoutingDefaultSkuTarget,
} from '../../api/useTenantRouting.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import { ConfirmDialog, IconButton, RadioGroup, Select, Tooltip } from '../../ui/index.ts';
import { DisabledNoteField } from '../tenant-routing/DisabledNoteField.tsx';
import { TryProbeButton } from '../tenant-routing/TryProbeButton.tsx';
import { disabledNoteForWire } from '../tenant-routing/disabledNote.ts';
import {
  immunityMinutes,
  isImmunityChoice,
  legAnchorMember,
  type ImmunityChoice,
} from './legModel.ts';
import type { LegState, RegionSlice } from './legPhysical.ts';
import { locateProbeMember, routingTargetWriteBody } from './routingTargetWrite.ts';

/** Probe state is region-local; only `jp` / `cn` are addressable through ops-api. */
function wireRegion(region: string): 'jp' | 'cn' | undefined {
  return region === 'jp' || region === 'cn' ? region : undefined;
}

type ResetChoice = 'all' | string;

/**
 * Which region(s) a reset targets by default: those carrying a removal
 * (armed or record), else those with a shadow verdict, else every region.
 */
export function defaultResetChoice(slices: readonly Pick<RegionSlice, 'region' | 'state'>[]): ResetChoice {
  const pick = (states: readonly LegState[]): readonly string[] =>
    slices.filter((entry) => states.includes(entry.state)).map((entry) => entry.region);
  const tripped = pick(['tripped', 'record']);
  const hot = tripped.length > 0 ? tripped : pick(['shadow']);
  return hot.length === 1 ? hot[0] ?? 'all' : 'all';
}

/** ops-api refuses a CN action when the Shanghai gateway is not wired; say that, not the raw error. */
function isCnNotConfigured(region: string, message: string): boolean {
  return region === 'cn' && message.includes('GATEWAY_CN_WORKER_BASE_URL');
}

interface RegionOutcome {
  readonly region: string;
  readonly ok: boolean;
  readonly detail: string;
}

/**
 * Reset / probe now / 试打 / manual disable (or 加回) for one physical leg.
 * Reset and probe-now name the region (`jp` / `cn`): ops-api sends `cn` to
 * the Shanghai gateway. A leg seen in both regions probes both and lets the
 * reset pick JP / CN / 两区.
 */
export function LegActions({ leg, state, targetId, regions, qualityBlindMinutes }: {
  /** Head slice: members, SKU, and the anchor member. */
  readonly leg: TargetProbeLegRecord;
  readonly state: LegState;
  readonly targetId: string;
  /** Region slices of this leg (order: jp, cn, others). */
  readonly regions: readonly Pick<RegionSlice, 'region' | 'state'>[];
  /** Minutes after the reset in which quality cannot remove the leg; null → the dialog states the formula. */
  readonly qualityBlindMinutes: number | null;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const reset = useResetTargetProbe();
  const probeNow = useProbeTargetNow();
  const setDefault = useSetRoutingDefaultSkuTarget();
  const setConfig = useSetRoutingConfigSkuTarget();
  const qc = useQueryClient();
  const [resetOpen, setResetOpen] = useState(false);
  const [immunity, setImmunity] = useState<ImmunityChoice>('5');
  const [resetChoice, setResetChoice] = useState<ResetChoice>('all');
  const [resetting, setResetting] = useState(false);
  const [probing, setProbing] = useState(false);
  const regionNames = regions.map((entry) => entry.region);
  const multiRegion = regionNames.length > 1;
  const regionTag = (region: string): string => region.toUpperCase();
  const outcomeText = (outcomes: readonly RegionOutcome[]): string =>
    outcomes.map((outcome) => `${regionTag(outcome.region)} ${outcome.detail}`).join(' · ');
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableNote, setDisableNote] = useState('');
  const [enableOpen, setEnableOpen] = useState(false);
  const [writing, setWriting] = useState(false);
  // Reset targets an enabled member first. Probe-now / 试打 also reach a
  // manually disabled one (the gateway only records the shot for it).
  const anchor = legAnchorMember(leg);
  const manual = state === 'manual';
  const busy = reset.isPending || probeNow.isPending || writing || resetting || probing;

  async function runReset(): Promise<void> {
    if (anchor === undefined) return;
    const targets = !multiRegion ? [regionNames[0] ?? ''] : resetChoice === 'all' ? regionNames : [resetChoice];
    setResetting(true);
    const outcomes: RegionOutcome[] = [];
    for (const region of targets) {
      const wire = wireRegion(region);
      try {
        const result = await reset.mutateAsync({
          layer: anchor.layer,
          target_id: anchor.target_id,
          immunity_minutes: immunityMinutes(immunity),
          ...(wire === undefined ? {} : { region: wire }),
        });
        outcomes.push({
          region,
          ok: true,
          detail: result.cleared ? t('guaranteed.legs.reset.region_cleared') : t('guaranteed.legs.reset.region_nothing'),
        });
      } catch (error) {
        const message = coerceErrorMessage(error);
        outcomes.push({
          region,
          ok: false,
          detail: isCnNotConfigured(region, message)
            ? t('guaranteed.legs.cn_not_configured')
            : t('guaranteed.legs.region_failed', { error: message }),
        });
      }
    }
    setResetting(false);
    if (!multiRegion && outcomes.every((outcome) => outcome.ok)) {
      toast.success(t('tenant_routing.probe.reset_done'));
    } else if (outcomes.every((outcome) => outcome.ok)) {
      toast.success(t('guaranteed.legs.reset.done_regions', { outcome: outcomeText(outcomes) }));
    } else {
      toast.error(multiRegion ? outcomeText(outcomes) : outcomes[0]?.detail ?? '');
    }
    if (outcomes.some((outcome) => outcome.ok)) setResetOpen(false);
  }

  async function runProbeNow(): Promise<void> {
    if (anchor === undefined) return;
    setProbing(true);
    const outcomes: RegionOutcome[] = [];
    for (const region of regionNames.length === 0 ? [''] : regionNames) {
      const wire = wireRegion(region);
      try {
        const result = await probeNow.mutateAsync({
          layer: anchor.layer,
          target_id: anchor.target_id,
          ...(wire === undefined ? {} : { region: wire }),
        });
        const code = result.state.last_status_code;
        outcomes.push({ region, ok: true, detail: code === null ? '—' : `HTTP ${String(code)}` });
      } catch (error) {
        const message = coerceErrorMessage(error);
        outcomes.push({
          region,
          ok: false,
          detail: isCnNotConfigured(region, message)
            ? t('guaranteed.legs.cn_not_configured')
            : t('guaranteed.legs.region_failed', { error: message }),
        });
      }
    }
    setProbing(false);
    const only = outcomes[0];
    if (!multiRegion && only !== undefined) {
      if (!only.ok) {
        toast.error(only.detail.length > 0 ? only.detail : t('guaranteed.leg.probe_now_done_nocode'));
        return;
      }
      toast.success(only.detail === '—'
        ? t('guaranteed.leg.probe_now_done_nocode')
        : t('guaranteed.leg.probe_now_done', { code: only.detail.replace('HTTP ', '') }));
      return;
    }
    const text = t('guaranteed.legs.probe_now_regions', { outcome: outcomeText(outcomes) });
    if (outcomes.every((outcome) => outcome.ok)) toast.success(text);
    else toast.error(text);
  }
  const resetLabel = state === 'shadow' ? t('guaranteed.leg.reset_shadow') : t('guaranteed.leg.reset');

  async function writeEnabled(enabled: boolean): Promise<void> {
    setWriting(true);
    const note = enabled ? undefined : disabledNoteForWire(disableNote);
    try {
      for (const member of leg.members) {
        const found = await locateProbeMember(member, leg.sku_id, fetchImpl);
        const body = found === null ? null : routingTargetWriteBody(found.target, enabled, note);
        if (found === null || body === null) {
          toast.error(t('tenant_routing.probe.disable_missing'));
          return;
        }
        if (found.layer === 'default') {
          await setDefault.mutateAsync({ skuId: leg.sku_id, body });
        } else {
          await setConfig.mutateAsync({ configId: found.configId, skuId: leg.sku_id, body });
        }
      }
      // The legs read carries enabled + note; refresh it so the lamp flips now.
      void qc.invalidateQueries({ queryKey: TARGET_PROBES_KEY });
      toast.success(t(enabled ? 'guaranteed.leg.enable_done' : 'tenant_routing.probe.disable_done'));
      setDisableOpen(false);
      setEnableOpen(false);
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    } finally {
      setWriting(false);
    }
  }

  return (
    <span className="inline-flex items-center justify-end gap-0.5 self-start">
      {manual ? null : (
        <Tooltip content={resetLabel}>
          <IconButton
            label={resetLabel}
            size="sm"
            disabled={busy || anchor === undefined}
            data-testid={`tenant-routing-target-probe-reset-${targetId}`}
            onClick={() => {
              setImmunity('5');
              setResetChoice(defaultResetChoice(regions));
              setResetOpen(true);
            }}
          >
            <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip content={t('guaranteed.leg.probe_now_tip')}>
        <IconButton
          label={t('guaranteed.leg.probe_now')}
          size="sm"
          disabled={busy || anchor === undefined}
          data-testid={`tenant-routing-target-probe-now-${targetId}`}
          onClick={() => { void runProbeNow(); }}
        >
          <Play aria-hidden="true" className="h-3.5 w-3.5" />
        </IconButton>
      </Tooltip>
      {anchor === undefined ? null : (
        <TryProbeButton layer={anchor.layer} targetId={anchor.target_id} disabled={busy} />
      )}
      {manual ? (
        <Tooltip content={t('guaranteed.leg.enable')}>
          <IconButton
            label={t('guaranteed.leg.enable')}
            size="sm"
            disabled={busy}
            data-testid={`guaranteed-leg-enable-${targetId}`}
            onClick={() => setEnableOpen(true)}
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip content={t('guaranteed.leg.disable')}>
          <IconButton
            label={t('guaranteed.leg.disable')}
            size="sm"
            disabled={busy}
            className="hover:text-bad"
            data-testid={`tenant-routing-target-probe-disable-${targetId}`}
            onClick={() => {
              setDisableNote('');
              setDisableOpen(true);
            }}
          >
            <Ban aria-hidden="true" className="h-3.5 w-3.5" />
          </IconButton>
        </Tooltip>
      )}
      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title={t('guaranteed.leg.reset_title')}
        confirmLabel={t('guaranteed.leg.reset')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        testId={`guaranteed-leg-reset-dialog-${targetId}`}
        loading={resetting}
        body={(
          <div className="flex flex-col gap-2">
            <p className="m-0">{t('guaranteed.leg.reset_body')}</p>
            <p className="m-0 text-paper-mid" data-testid={`guaranteed-leg-reset-quality-${targetId}`}>
              {qualityBlindMinutes === null
                ? t('guaranteed.leg.reset_quality_blind_formula')
                : t('guaranteed.leg.reset_quality_blind', { minutes: String(qualityBlindMinutes) })}
            </p>
            {multiRegion ? (
              <>
                <span className="text-label text-paper-low">{t('guaranteed.legs.reset.region')}</span>
                <RadioGroup
                  value={resetChoice}
                  onValueChange={setResetChoice}
                  orientation="horizontal"
                  ariaLabel={t('guaranteed.legs.reset.region')}
                  testId={`guaranteed-leg-reset-region-${targetId}`}
                  options={[
                    ...regions.map((entry) => ({ value: entry.region, label: regionTag(entry.region) })),
                    { value: 'all', label: t('guaranteed.legs.reset.region_all') },
                  ]}
                />
              </>
            ) : null}
            <span className="text-label text-paper-low">{t('guaranteed.leg.immunity')}</span>
            <Select
              value={immunity}
              onValueChange={(value) => {
                if (isImmunityChoice(value)) setImmunity(value);
              }}
              options={[
                { value: '0', label: t('guaranteed.leg.immunity.0') },
                { value: '5', label: t('guaranteed.leg.immunity.5') },
                { value: '10', label: t('guaranteed.leg.immunity.10') },
                { value: '30', label: t('guaranteed.leg.immunity.30') },
                { value: '60', label: t('guaranteed.leg.immunity.60') },
              ]}
              ariaLabel={t('guaranteed.leg.immunity')}
              testId={`guaranteed-leg-immunity-${targetId}`}
            />
          </div>
        )}
        onConfirm={() => { void runReset(); }}
      />
      <ConfirmDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        tone="danger"
        title={t('tenant_routing.probe.disable_title')}
        body={(
          <div className="flex flex-col gap-3">
            <p className="m-0">{t('tenant_routing.probe.disable_body')}</p>
            <DisabledNoteField
              value={disableNote}
              onChange={setDisableNote}
              fieldId={`guaranteed-leg-disable-note-${targetId}`}
              testId={`guaranteed-leg-disable-note-${targetId}`}
            />
          </div>
        )}
        confirmLabel={t('tenant_routing.probe.disable_confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={writing}
        testId={`tenant-routing-target-probe-disable-dialog-${targetId}`}
        onConfirm={() => { void writeEnabled(false); }}
      />
      <ConfirmDialog
        open={enableOpen}
        onOpenChange={setEnableOpen}
        title={t('guaranteed.leg.enable_title')}
        body={t('guaranteed.leg.enable_body')}
        confirmLabel={t('guaranteed.leg.enable')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={writing}
        testId={`guaranteed-leg-enable-dialog-${targetId}`}
        onConfirm={() => { void writeEnabled(true); }}
      />
    </span>
  );
}
